// "Last Method step" detection for the next-video reveal.
//
// No such helper existed. It must mirror MethodSwiper._getSlides() exactly
// (app/src/components/shopwindow/MethodSwiper.js): rendered slides =
// (opening slide ? 1 : 0) + min(getClusterSize(clusterName, 0), visibleMethodSteps).
// ShopWindowConfig.slideCount/range use getTotalClusterSize and IGNORE the
// visibleMethodSteps cap, so they can't be used to find the real last slide.
//
// `visibleMethodSteps` is the app-config cap (config.getVisibleMethodSteps(),
// undefined = no cap). Passed in so this stays pure and unit-testable.

import { experienceTypes } from "@/modules/ExperienceData";
import { openingSlideType } from "@/plugin/ShopWindowConfig";
import { chainStore } from "@/stores/chain/store";
import appConfig from "@/modules/config";
import { valueNames } from "@/modules/Constants";
import type ApplicationData from "@/modules/ApplicationData";
import type ExperienceData from "@/modules/ExperienceData";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type ShopWindowConfigList from "@/plugin/ShopWindowConfigList";

// Shop-window config for a cluster, from the LIST by cluster name — not gated
// on which tab is the active shop window. Shared by handleTimeSync (step-index
// tracking) and findMethodConfig so the list scan lives in one place.
export function findShopWindowConfigByCluster(
  applicationData: ApplicationData,
  clusterName: string,
): ShopWindowConfig | null {
  const list = applicationData.getValue(valueNames.SHOP_WINDOW_CONFIG_LIST) as
    | ShopWindowConfigList
    | undefined;
  if (!list || !list.count) {
    return null;
  }
  for (let n = 0; n < list.count; n++) {
    const c = list.atIndex(n);
    if (c && c.clusterName === clusterName) {
      return c;
    }
  }
  return null;
}

// The Method entry in the shop-window config list (clusterName === 'method'), or
// null. Shared by McpMethodTimeline (active step) and McpPlayer (chain reveal) so
// the two lookups can't drift.
export function findMethodConfig(
  applicationData: ApplicationData,
): ShopWindowConfig | null {
  return findShopWindowConfigByCluster(applicationData, "method");
}

// Whether MethodSwiper actually renders an opening slide — mirrors
// MethodSwiper._getOpeningSlide(): a CUSTOM opening renders ONLY for RECIPE-type
// experiences; HINTS always renders; NONE never. config.hasOpeningSlide() is NOT
// sufficient — it's true for CUSTOM regardless of experience type, so it would
// over-count the opening slot for a CUSTOM opening on a non-recipe experience,
// inflating the last-slide index so isLastMethodStep never fires.
function rendersOpeningSlide(
  config: ShopWindowConfig,
  experienceData: ExperienceData,
): boolean {
  const type = config.openingSlideType();
  if (type === openingSlideType.CUSTOM) {
    return experienceData.type === experienceTypes.RECIPE;
  }
  return type === openingSlideType.HINTS;
}

export function lastRenderedMethodSlideIndex(
  config: ShopWindowConfig,
  experienceData: ExperienceData,
  visibleMethodSteps: number | null | undefined,
): number {
  const opening = rendersOpeningSlide(config, experienceData) ? 1 : 0;
  let size = experienceData.getClusterSize(config.clusterName, 0);
  if (visibleMethodSteps !== undefined && visibleMethodSteps !== null) {
    size = Math.min(size, visibleMethodSteps);
  }
  return opening + size - 1;
}

export function isLastMethodStep(
  config: ShopWindowConfig | null | undefined,
  experienceData: ExperienceData | null | undefined,
  visibleMethodSteps: number | null | undefined,
): boolean {
  if (!config || !experienceData) {
    return false;
  }
  return (
    config.index ===
    lastRenderedMethodSlideIndex(config, experienceData, visibleMethodSteps)
  );
}

// Whether the MCP "Up Next" section reveals: the chain is ready with at least
// one pick AND we're on the last rendered Method step. McpUpNext reveals on
// this, and McpMethodTimeline collapses the Method list on the same condition —
// shared here so the two can't drift apart. Unlike the pure helpers
// above, this reads the live chain store + app config, so it's the impure
// composition wrapper, not a unit-testable primitive.
export function isUpNextRevealing(
  config: ShopWindowConfig | null | undefined,
  experienceData: ExperienceData | null | undefined,
): boolean {
  const chainReady =
    chainStore.getStatus() === "ready" &&
    (chainStore.getChain() || []).length > 0;
  if (!chainReady) {
    return false;
  }
  return isLastMethodStep(
    config,
    experienceData,
    appConfig.getVisibleMethodSteps(),
  );
}
